import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { brandsData } from '../data/siteData';
import { imageUrl } from '../lib/images';

export default function BrandsPage() {
  const [brands, setBrands] = useState(brandsData);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetch('/_api/public/v2/content/brands?limit=100')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente brands');
        return res.json();
      })
      .then((data) => {
        if (data?.items && Array.isArray(data.items)) {
          const cmsItems = data.items.filter((b) => b.active);
          const merged = [...brandsData];
          cmsItems.forEach((cmsBrand) => {
            const index = merged.findIndex(
              (b) => b.name.toLowerCase().trim() === cmsBrand.name.toLowerCase().trim()
            );
            if (index >= 0) {
              merged[index] = { ...merged[index], ...cmsBrand };
            } else {
              merged.push(cmsBrand);
            }
          });
          setBrands(merged);
        }
      })
      .catch((err) => {
        console.warn('Anvender lokale forhandlerdata:', err);
      })
      .finally(() => setLoading(false));
  }, []);

  const smykkeBrands = brands.filter((b) => b.category === 'smykker');
  const ureBrands = brands.filter((b) => b.category === 'ure');

  return (
    <>
      <Helmet>
        <title>Vores Forhandlere & Brands - Brønshøj Ure & Guld</title>
        <meta
          name="description"
          content="Se vores udvalg af forhandlere af smykker og kvalitetsure hos Brønshøj Ure & Guld. Bl.a. NURAN, Lund Copenhagen, Siersbøl, Jaguar, Festina, Citizen og Obaku."
        />
        <meta property="og:title" content="Vores Forhandlere & Brands - Brønshøj Ure & Guld" />
        <meta
          property="og:description"
          content="Se vores forhandlere af smykker og kvalitetsure hos Brønshøj Ure & Guld."
        />
        <meta
          property="og:image"
          content="https://assets.auxodev.dk/assets/807b5052-bea6-494e-8947-b0c29529a13c/elegant-black-and-white-logo-design-2.png"
        />
      </Helmet>

      {/* Header */}
      <section className="bg-[#0B132B] text-white pt-36 sm:pt-40 lg:pt-56 pb-16 sm:pb-20 lg:pb-28 border-b border-white/10 text-center">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <span className="text-xs uppercase tracking-[0.3em] text-[#B5A897] font-semibold block">
            ANERKENDTE KVALITETSPARTNERE
          </span>
          <h1 className="font-serif text-3xl sm:text-5xl uppercase tracking-[0.2em] font-normal text-white">
            VORES FORHANDLERE & BRANDS
          </h1>
          <p className="text-xs sm:text-sm text-stone-300 max-w-2xl mx-auto uppercase tracking-widest font-sans leading-relaxed">
            Hos Brønshøj Ure & Guld fører vi et nøje udvalgt sortiment af anerkendte mærker inden for smykker og kvalitetsure.
          </p>
        </div>
      </section>

      <section className="py-20 bg-[#F9F8F6]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin h-8 w-8 border border-[#B5A897] border-t-transparent"></div>
            </div>
          ) : (
            <>
              {/* Smykkebrands */}
              <div className="space-y-8">
                <div className="text-center max-w-xl mx-auto mb-8 space-y-2">
                  <div className="w-[1px] h-8 bg-[#B5A897] mx-auto mb-3"></div>
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#B5A897] font-semibold block">
                    SMYKKEKOLLEKTIONER
                  </span>
                  <h2 className="font-serif text-2xl sm:text-3xl font-normal uppercase tracking-[0.15em] text-[#1A1A1A]">
                    SMYKKER VI FORHANDLER
                  </h2>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
                  {smykkeBrands.map((brand) => {
                    const logo = brand.logo_url && (Array.isArray(brand.logo_url) ? brand.logo_url[0] : brand.logo_url);
                    const CardTag = brand.link ? Link : 'div';
                    const cardProps = brand.link ? { to: brand.link } : {};

                    return (
                      <CardTag
                        key={brand.id || brand.name}
                        {...cardProps}
                        className={`group bg-[#F2EFE9] hover:bg-[#ECE8E0] p-4 sm:p-5 border border-[#E2DDD3] hover:border-[#B5A897] flex flex-col items-center justify-center text-center min-h-44 h-auto rounded-none shadow-none transition-all duration-300 relative ${
                          brand.link ? 'cursor-pointer' : ''
                        }`}
                      >
                        {logo ? (
                          <div className="h-16 flex items-center justify-center w-full">
                            <img
                              src={imageUrl(logo, 'width=320,quality=90,format=auto')}
                              alt={brand.name}
                              className="max-h-14 w-auto max-w-[140px] object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300"
                            />
                          </div>
                        ) : (
                          <div className="h-16 flex items-center justify-center px-2">
                            <span className="font-serif text-base sm:text-lg font-normal uppercase tracking-widest text-[#1A1A1A] group-hover:text-[#B5A897] transition-colors leading-snug">
                              {brand.name}
                            </span>
                          </div>
                        )}
                        <span className="mt-3 text-[10px] font-medium text-stone-500 uppercase tracking-[0.2em] block max-w-full leading-relaxed break-words px-1">
                          {logo ? brand.name : (brand.subtitle || brand.description || '')}
                        </span>
                      </CardTag>
                    );
                  })}
                </div>
              </div>

              {/* Urebrands */}
              <div className="space-y-8 pt-6">
                <div className="text-center max-w-xl mx-auto mb-8 space-y-2">
                  <div className="w-[1px] h-8 bg-[#B5A897] mx-auto mb-3"></div>
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#B5A897] font-semibold block">
                    UR-KOLLEKTIONER
                  </span>
                  <h2 className="font-serif text-2xl sm:text-3xl font-normal uppercase tracking-[0.15em] text-[#1A1A1A]">
                    URE VI FORHANDLER
                  </h2>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
                  {ureBrands.map((brand) => {
                    const logo = brand.logo_url && (Array.isArray(brand.logo_url) ? brand.logo_url[0] : brand.logo_url);
                    const CardTag = brand.link ? Link : 'div';
                    const cardProps = brand.link ? { to: brand.link } : {};

                    return (
                      <CardTag
                        key={brand.id || brand.name}
                        {...cardProps}
                        className={`group bg-[#F2EFE9] hover:bg-[#ECE8E0] p-6 border border-[#E2DDD3] hover:border-[#B5A897] flex flex-col items-center justify-center text-center min-h-44 h-auto rounded-none shadow-none transition-all duration-300 ${
                          brand.link ? 'cursor-pointer' : ''
                        }`}
                      >
                        {logo ? (
                          <div className="h-16 flex items-center justify-center w-full">
                            <img
                              src={imageUrl(logo, 'width=320,quality=90,format=auto')}
                              alt={brand.name}
                              className="max-h-14 w-auto max-w-[140px] object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300"
                            />
                          </div>
                        ) : (
                          <div className="h-16 flex items-center justify-center px-2">
                            <span className="font-serif text-base sm:text-lg font-normal uppercase tracking-widest text-[#1A1A1A] group-hover:text-[#B5A897] transition-colors leading-snug">
                              {brand.name}
                            </span>
                          </div>
                        )}
                        <span className="mt-3 text-[10px] font-medium text-stone-500 uppercase tracking-[0.2em] block max-w-full leading-relaxed break-words px-1">
                          {logo ? brand.name : (brand.subtitle || brand.description || '')}
                        </span>
                      </CardTag>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
